import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Paper, Typography } from '@mui/material';
import { ArrowForward, ExpandMore } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { categoryLandingPath, countyLabel } from '../../utils/landingPaths';

// **félkövér** → <strong>; minden más sima szöveg (nincs HTML-értelmezés, így nem lehet befecskendezni)
const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );

/**
 * A kategória SEO-szövegének egyszerű jelölése (lásd backend/prisma/schema.prisma Category.seoContent):
 * üres sor = bekezdés, "## " = alcím, "- " sorok = felsorolás, **félkövér**.
 */
export const SeoRichText = ({ text }) => {
  const blocks = String(text || '').replace(/\r/g, '').split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, index) => {
    const lines = block.split('\n');
    if (lines[0].startsWith('## ')) {
      const rest = lines.slice(1).join('\n').trim();
      return (
        <React.Fragment key={index}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: 'primary.dark', mt: 3, mb: 1 }}>
            {lines[0].slice(3)}
          </Typography>
          {rest && <SeoRichText text={rest} />}
        </React.Fragment>
      );
    }
    if (lines.every((line) => line.startsWith('- '))) {
      return (
        <Box key={index} component="ul" sx={{ pl: 3, my: 1.5, '& li': { mb: 0.75, lineHeight: 1.7 } }}>
          {lines.map((line, i) => <li key={i}><Typography component="span" variant="body1">{renderInline(line.slice(2))}</Typography></li>)}
        </Box>
      );
    }
    return (
      <Typography key={index} variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, color: 'text.primary' }}>
        {renderInline(lines.join(' '))}
      </Typography>
    );
  });
};

/**
 * Kategória landing oldal szövege és GYIK-je a szolgáltatói lista alatt.
 * Csak-kategória oldalon a teljes szöveg + GYIK (FAQPage JSON-LD-vel); kategória × megye oldalon csak az első
 * bekezdés és egy link a kategória oldalra, hogy ugyanaz a hosszú szöveg ne ismétlődjön ~20 megyeoldalon.
 */
const CategorySeoContent = ({ category, county }) => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    if (!category?.slug) return undefined;
    apiService
      .get(API_ENDPOINTS.CATEGORIES.SEO_CONTENT(category.slug))
      .then((response) => { if (!cancelled && response.success) setContent(response.data.category); })
      .catch(() => {}); // szöveg nélkül is működik az oldal
    return () => { cancelled = true; };
  }, [category?.slug]);

  if (!content?.seoContent) return null;

  const faq = Array.isArray(content.seoFaq) ? content.seoFaq : [];
  const firstParagraph = content.seoContent.replace(/\r/g, '').split(/\n\s*\n/)[0];

  if (county) {
    return (
      <Paper elevation={0} sx={{ mt: 4, p: { xs: 2.5, md: 4 }, borderRadius: '20px', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: 'primary.dark', mb: 1.5 }}>
          {content.name} – {countyLabel(county)}
        </Typography>
        <SeoRichText text={firstParagraph} />
        <Button component={RouterLink} to={categoryLandingPath(content)} endIcon={<ArrowForward />} sx={{ mt: 1, fontWeight: 600 }}>
          Tudj meg többet – {content.name}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ mt: 4, p: { xs: 2.5, md: 4 }, borderRadius: '20px', border: '1px solid', borderColor: 'divider' }}>
      {faq.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faq.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer },
            })),
          })}</script>
        </Helmet>
      )}
      <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: 'primary.dark', mb: 2 }}>
        Amit érdemes tudni – {content.name}
      </Typography>
      <Box sx={{ maxWidth: 820 }}>
        <SeoRichText text={content.seoContent} />
      </Box>

      {faq.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.dark', mb: 1.5 }}>
            Gyakori kérdések
          </Typography>
          {faq.map((item, index) => (
            <Accordion key={index} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:not(:last-of-type)': { borderBottom: 0 }, '&::before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                {/* Az MUI v7 AccordionSummary maga <h3>-ba csomagolja a kérdést */}
                <Typography component="span" sx={{ fontWeight: 600 }}>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default CategorySeoContent;
